import { GeoPicker, Select } from "components/shared/form";
import { bidPointTypesEnum, featureModuleTypesEnum } from "helpers/enums";
import { ymEvent } from "helpers/system";
import { requiredField } from "helpers/validators";
import React, { useState } from "react";
import { components } from "react-select";
import { list as pointsList } from "sources/books/counterpartyPoints";
import { getPostamatPointsList } from "sources/books/postamatPoints";
import CounterpartyAddresses from "./counterpartyAddresses";
import PostamatAddresses from "./postamatAddresses";

const CargoOwnerPointOption = (props) => {
    const { name, geozone } = props.data.option;

    return (
        <components.Option {...props}>
            {name}
            <small className="text-muted pl-1">{geozone.address}</small>
        </components.Option>
    );
};

const CargoOwnerPointValueContainer = (props) => {
    const { children, getValue, hasValue } = props;

    if (!hasValue) {
        return <components.ValueContainer {...props} />;
    }

    const value = getValue()[0];

    if (!value || !value.option) {
        return <components.ValueContainer {...props} />;
    }

    const { geozone } = value.option;

    return (
        <components.ValueContainer {...props}>
            {!props.selectProps.inputValue && (
                <components.SingleValue {...props}>
                    <div>
                        {value.label}
                        <small className="text-muted pl-1">{geozone.address}</small>
                    </div>
                </components.SingleValue>
            )}
            {children[children.length - 1]}
        </components.ValueContainer>
    );
};

const ACTIONS = {
    INIT: "INIT",
    SELECT_COUNTERPARTY_IN_SELECT: "SELECT-COUNTERPARTY-IN-SELECT",
    SELECT_COUNTERPARTY_IN_TABLE: "SELECT-COUNTERPARTY-IN-TABLE",
    SELECT_POSTAMAT_IN_SELECT: "SELECT-POSTAMAT-IN-SELECT",
    SELECT_POSTAMAT_IN_TABLE: "SELECT-POSTAMAT-IN-TABLE",
};

const AddressPicker = (props) => {
    const {
        isDisabled,
        i,
        onChange,
        counterpartiesData,
        geozone,
        switchMap,
        bidCounterpartyId,
        cargoOwnerDictionaryItemId,
        bidPointType,
        inFinishedStatus,
        availableFeatureModules,
        withoutPostamat,
        additionalInputBottomText,
    } = props;

    const [action, setAction] = useState(ACTIONS.INIT);
    const [counterpartyId, setCounterpartyId] = useState(null);
    const [addressSelectValue, setAddressSelectValue] = useState(null);
    const [pointSaved, setPointSaved] = useState(false);

    const isCustomPoint = bidPointType === bidPointTypesEnum.CustomPoint;

    const saveCounterpartyAddress = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        const counterpartyOptions = [];

        if (bidCounterpartyId) {
            counterpartyOptions.push({ label: counterpartiesData[bidCounterpartyId]?.name, value: bidCounterpartyId });
        }
        if (cargoOwnerDictionaryItemId && bidCounterpartyId !== cargoOwnerDictionaryItemId) {
            counterpartyOptions.push({
                label: counterpartiesData[cargoOwnerDictionaryItemId]?.name,
                value: cargoOwnerDictionaryItemId,
            });
        }

        props.modal({
            name: "counterpartyPointForm",
            title: "Сохранение адреса для контрагента",
            geozone,
            counterpartyOptions,
            successCallback: (res) => {
                if (res) {
                    if (geozone.coordinates) {
                        ymEvent("CreateCounerpartyPointWithPolygon");
                    }
                    setPointSaved(true);
                    setAddressSelectValue(res.id);
                    return Promise.resolve(true);
                } else {
                    return Promise.resolve(false);
                }
            },
        });
    };

    const selectCounterparty = (e, id) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        setCounterpartyId(id);
        setAction(ACTIONS.SELECT_COUNTERPARTY_IN_SELECT);
    };

    const selectPostamat = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        setCounterpartyId(null);
        setAction(ACTIONS.SELECT_POSTAMAT_IN_SELECT);
    };

    const onChangeGeozone = ({ name, value }) => {
        setAction(ACTIONS.INIT);
        setAddressSelectValue(null);
        setPointSaved(false);

        onChange({ name, value });

        setTimeout(() => onChange({ name: "counterpartyPointId", value: null }), 0);
    };

    const selectOnMap = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        setAction(ACTIONS.INIT);
        setCounterpartyId(null);

        if (isDisabled) {
            return;
        }

        const callback = (value) => {
            if (value && Object.keys(value).length) {
                onChangeGeozone({ name: "geozone", value });
            }

            switchMap({ mode: "main", callback: null });
        };

        switchMap({ mode: "picker", value: geozone, callback });
    };

    const back = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        setAction(ACTIONS.INIT);
    };

    const setCounterpartyAddressValue = ({ name, value }) => {
        onChange({ name, value: value?.geozone });
        setTimeout(() => onChange({ name: "counterpartyPointId", value: value?.id }), 0);
    };
    const openCounterpartyAddressesModal = () => {
        props.modal({
            name: "default",
            title: "Список адресов контрагента",
            CustomBody: CounterpartyAddresses,
            windowMod: "lg d-flex",
            bodyData: {
                onChange: setCounterpartyAddressValue,
                counterpartyId,
            },
        });
    };

    const setPostamatAddressValue = ({ name, value }) => {
        onChange({ name, value: value?.geozone });

        setTimeout(() => onChange({ name: "counterpartyPointId", value: null }), 0);
    };
    const openPostamatAddressesModal = () => {
        props.modal({
            name: "default",
            title: "Список постаматов",
            CustomBody: PostamatAddresses,
            windowMod: "lg d-flex",
            bodyData: {
                onChange: setPostamatAddressValue,
            },
        });
    };

    switch (action) {
        case ACTIONS.INIT: {
            const showSavePoint = !pointSaved && geozone?.address;
            const showSelectPostamat =
                availableFeatureModules.includes(featureModuleTypesEnum.DocumentAssignment) &&
                isCustomPoint &&
                !withoutPostamat;
            const showSelectBidCounterparty = counterpartiesData[bidCounterpartyId]?.hasPoints && !inFinishedStatus;
            const showSelectCargoOwnerDictionaryItem =
                counterpartiesData[cargoOwnerDictionaryItemId]?.hasPoints &&
                bidCounterpartyId !== cargoOwnerDictionaryItemId &&
                !inFinishedStatus;

            const inputBottomText = (
                <>
                    {additionalInputBottomText}
                    {(bidCounterpartyId || cargoOwnerDictionaryItemId) && (
                        <>
                            {showSelectBidCounterparty && (
                                <small
                                    className="link d-block"
                                    onClick={(e) => selectCounterparty(e, bidCounterpartyId)}
                                >
                                    Выбрать из списка адресов контрагента &quot;
                                    {counterpartiesData[bidCounterpartyId]?.name}&quot;
                                </small>
                            )}
                            {showSelectCargoOwnerDictionaryItem && (
                                <small
                                    className="link d-block"
                                    onClick={(e) => selectCounterparty(e, cargoOwnerDictionaryItemId)}
                                >
                                    Выбрать из списка адресов{" "}
                                    {bidPointType === bidPointTypesEnum.LoadPoint
                                        ? "грузоотправителя"
                                        : "грузополучателя"}
                                    &nbsp;&quot;{counterpartiesData[cargoOwnerDictionaryItemId]?.name}&quot;
                                </small>
                            )}
                            {showSavePoint && (
                                <small className="link d-block" onClick={saveCounterpartyAddress}>
                                    Сохранить адрес для контрагента
                                </small>
                            )}
                        </>
                    )}
                    {showSelectPostamat && (
                        <small className="link d-block" onClick={selectPostamat}>
                            Выбрать из списка постаматов
                        </small>
                    )}
                </>
            );

            return (
                <GeoPicker
                    label="Адрес"
                    name="geozone"
                    index={i}
                    switchMap={switchMap}
                    onChange={onChangeGeozone}
                    value={geozone}
                    disabled={isDisabled}
                    validators={[requiredField]}
                    inputBottomText={inputBottomText}
                />
            );
        }
        case ACTIONS.SELECT_COUNTERPARTY_IN_SELECT: {
            const selectCounterpartyInTable = (e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();

                setAction(ACTIONS.SELECT_COUNTERPARTY_IN_TABLE);

                openCounterpartyAddressesModal();
            };

            const setAddressValueWithOption = ({ name, value, option }) => {
                setAddressSelectValue(value);

                onChange({ name, value: option.geozone });

                setTimeout(() => onChange({ name: "counterpartyPointId", value }), 0);
            };

            const inputBottomText = (
                <>
                    <small className="link d-block" onClick={selectCounterpartyInTable}>
                        Выбрать адрес контрагента в табличном представлении
                    </small>
                    <small className="link d-block" onClick={selectOnMap}>
                        Выбрать на карте
                    </small>
                    <small className="link d-block" onClick={back}>
                        {`< Назад`}
                    </small>
                </>
            );

            return (
                <Select
                    wrapper="book-field"
                    fieldWrapper="col-sm-8 col-md-9"
                    inputProps={{
                        name: "geozone",
                    }}
                    propName="name"
                    url={(props) => pointsList({ counterpartyId, ...props })}
                    onChange={setAddressValueWithOption}
                    value={addressSelectValue}
                    required
                    unClearable
                    validators={[requiredField]}
                    disabled={isDisabled}
                    label="Адрес"
                    inputBottomText={inputBottomText}
                    components={{ Option: CargoOwnerPointOption, ValueContainer: CargoOwnerPointValueContainer }}
                />
            );
        }
        case ACTIONS.SELECT_COUNTERPARTY_IN_TABLE: {
            const selectCounterpartyInSelect = (e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();

                setAction(ACTIONS.SELECT_COUNTERPARTY_IN_SELECT);
            };

            const updateGeozoneInPlace = ({ name, value }) => {
                onChange({ name, value });
                setTimeout(() => onChange({ name: "counterpartyPointId", value: null }), 0);
            };

            const inputBottomText = (
                <>
                    <small className="link d-block" onClick={selectCounterpartyInSelect}>
                        Выбрать адрес контрагента из списка
                    </small>
                    <small className="link d-block" onClick={selectOnMap}>
                        Выбрать на карте
                    </small>
                    <small className="link d-block" onClick={back}>
                        {`< Назад`}
                    </small>
                </>
            );

            return (
                <GeoPicker
                    label="Адрес"
                    name="geozone"
                    index={i}
                    switchMap={() => openCounterpartyAddressesModal()}
                    onChange={updateGeozoneInPlace}
                    value={geozone}
                    disabled={isDisabled}
                    validators={[requiredField]}
                    inputBottomText={inputBottomText}
                />
            );
        }
        case ACTIONS.SELECT_POSTAMAT_IN_SELECT: {
            const selectPostamatInTable = (e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();

                setAction(ACTIONS.SELECT_POSTAMAT_IN_TABLE);

                openPostamatAddressesModal();
            };

            const setAddressValueWithOption = ({ name, value, option }) => {
                setAddressSelectValue(value);

                onChange({ name, value: option.geozone });

                setTimeout(() => onChange({ name: "counterpartyPointId", value: null }), 0);
            };

            const inputBottomText = (
                <>
                    <small className="link d-block" onClick={selectPostamatInTable}>
                        Выбрать адрес постамата в табличном представлении
                    </small>
                    <small className="link d-block" onClick={selectOnMap}>
                        Выбрать на карте
                    </small>
                    <small className="link d-block" onClick={back}>
                        {`< Назад`}
                    </small>
                </>
            );

            return (
                <Select
                    wrapper="book-field"
                    fieldWrapper="col-sm-8 col-md-9"
                    inputProps={{
                        name: "geozone",
                    }}
                    propName="name"
                    url={getPostamatPointsList}
                    onChange={setAddressValueWithOption}
                    value={addressSelectValue}
                    required
                    unClearable
                    validators={[requiredField]}
                    disabled={isDisabled}
                    label="Адрес"
                    inputBottomText={inputBottomText}
                    components={{ Option: CargoOwnerPointOption, ValueContainer: CargoOwnerPointValueContainer }}
                />
            );
        }
        case ACTIONS.SELECT_POSTAMAT_IN_TABLE: {
            const selectPostamatInSelect = (e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();

                setAction(ACTIONS.SELECT_POSTAMAT_IN_SELECT);
            };

            const inputBottomText = (
                <>
                    <small className="link d-block" onClick={selectPostamatInSelect}>
                        Выбрать адрес постамата из списка
                    </small>
                    <small className="link d-block" onClick={selectOnMap}>
                        Выбрать на карте
                    </small>
                    <small className="link d-block" onClick={back}>
                        {`< Назад`}
                    </small>
                </>
            );

            return (
                <GeoPicker
                    label="Адрес"
                    name="geozone"
                    index={i}
                    switchMap={() => openPostamatAddressesModal()}
                    onChange={setPostamatAddressValue}
                    value={geozone}
                    disabled={isDisabled}
                    validators={[requiredField]}
                    inputBottomText={inputBottomText}
                />
            );
        }
        default: {
            return null;
        }
    }
};

export default AddressPicker;
